import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { OnPremITLaborCashFlow, OnPremOtherCostsOutput } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Container, Header, SpaceBetween, Spinner, Table, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../../common/Constants';
import { constants } from '../../../../common/Localization';
import { MatchParam } from '../../../../common/types';
import { HelpPanelContext } from '../../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import { commonTotalCostSummary, generateCashFlowTable, summaryColumnDefs } from '../../common/Common';
import { CashFlowData, CommonSummaryItem } from '../../common/types';

const tcoService = new TcoService();
type Props = FlashContextInterface;
function OnpremITLaborCost({ flash }: Props): JSX.Element {
    const [ loading, setLoading ] = useState(false);
    const { assessment, tcoLoading } = useContext(TcoContext);
    const [ onPremiseITLaborOutput, setOnPremiseITLaborOutput ] = useState<OnPremOtherCostsOutput | null>(null);
    const [ currency, setCurrency ] = useState('');
    const { panelHandler } = useContext(HelpPanelContext);

    const CASH_FLOW_TAB_ID = 'cashFlow';
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const isMounted = useIsMounted();
    const loadOnPremiseData = async () => {
        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const onPremAssessment = await tcoService.getOnPremise(tcoAssessmentId);
                if (isMounted()) {
                    setOnPremiseITLaborOutput(onPremAssessment.output.onPremises.others);
                    setCurrency(onPremAssessment.output.onPremises.currency);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_ONPREM);
            }
        } finally {
            if (isMounted()) { 
                setLoading(false);
            }
        }
    };

    const buildITLaborSummaryItems = (itLaborOutput: OnPremOtherCostsOutput): CommonSummaryItem[] => [
        {
            type: constants.ADMIN_SERVER,
            description: itLaborOutput.serverAdminCost.desc,
            cost: itLaborOutput.serverAdminCost.value,
        }, {
            type: constants.ADMIN_STORAGE,
            description: itLaborOutput.storageAdminCost.desc,
            cost: itLaborOutput.storageAdminCost.value,
        }, {
            type: constants.ADMIN_NETWORK,
            description: itLaborOutput.networkAdminCost.desc,
            cost: itLaborOutput.networkAdminCost.value,
        }, {
            type: constants.ADMIN_DATABASE,
            description: itLaborOutput.databaseAdminCost.desc,
            cost: itLaborOutput.databaseAdminCost.value,
        },
    ];

    const buildTabs = (itLaborOutput: OnPremOtherCostsOutput): TabsProps.Tab[] => [{
        id: CASH_FLOW_TAB_ID,
        label: constants.CASH_FLOW_NAME,
        content: generateCashFlowTable(
            generateITLaborCashFlowItems(itLaborOutput.cashFlow.yearly),
            itLaborOutput.cashFlow.yearly.total.length,
            currency,
            constants.CASH_FLOW_NAME,
        ),
    }];

    const generateITLaborCashFlowItems = (itLaborCashflow: OnPremITLaborCashFlow): CashFlowData[] => {
        const serverAdminCost = {
            type: constants.ADMIN_SERVER,
        };
        const storageAdminCost = {
            type: constants.ADMIN_STORAGE,
        };
        const networkAdminCost = {
            type: constants.ADMIN_NETWORK,
        };
        const databaseAdminCost = {
            type: constants.ADMIN_DATABASE,
        };
        const totalCost = {
            type: constants.TOTAL,
            highlight: true,
        };

        const totalYears = itLaborCashflow.total.length;
        for (let year = 0; year < totalYears; year++) {
            (serverAdminCost as CashFlowData)['year' + year] = itLaborCashflow.serverAdminCost[year];
            (storageAdminCost as CashFlowData)['year' + year] = itLaborCashflow.storageAdminCost[year];
            (networkAdminCost as CashFlowData)['year' + year] = itLaborCashflow.networkAdminCost[year];
            (databaseAdminCost as CashFlowData)['year' + year] = itLaborCashflow.databaseAdminCost[year];
            (totalCost as CashFlowData)['year' + year] = itLaborCashflow.total[year];
        }
      
        return [
            serverAdminCost as CashFlowData,
            storageAdminCost as CashFlowData,
            networkAdminCost as CashFlowData,
            databaseAdminCost as CashFlowData,
            totalCost as CashFlowData,
        ];
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ONPREM_IT_LABOR);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadOnPremiseData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const buildOnPremITLaborContent = (): JSX.Element => {
        if (tcoLoading || loading || !onPremiseITLaborOutput) {
            return (
                <Spinner size='big' />
            );
        }

        return (
            <SpaceBetween size='l'>
                <Header variant='h1'>{constants.ON_PREM_IT_LABOR_TITLE}</Header>
                <Table
                    columnDefinitions={summaryColumnDefs(currency)}
                    items={buildITLaborSummaryItems(onPremiseITLaborOutput)}
                    sortingDisabled
                />
                {commonTotalCostSummary(
                    currency,
                    onPremiseITLaborOutput.subTotalCost.value,
                    onPremiseITLaborOutput.manualAdjustmentCost.value,
                    onPremiseITLaborOutput.totalCost.value,
                    onPremiseITLaborOutput.manualAdjustmentCost.desc,
                )}
                <Tabs tabs={buildTabs(onPremiseITLaborOutput)} activeTabId={CASH_FLOW_TAB_ID}></Tabs>
            </SpaceBetween>
        );
    };
    return (
        <Container data-testid='container'>
            {buildOnPremITLaborContent()}
        </Container>
    );
};

export default withFlashContext(OnpremITLaborCost);
